"use client";

import { useState, useEffect } from "react";
import { useRouter } from "next/navigation";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Loader2, Search } from "lucide-react";
import { toast } from "sonner";
import { useOAuthErrorHandler } from "@/hooks/use-oauth-error-handler";
import OAuthReauthDialog from "@/components/shared/oauth-reauth-dialog";
import { searchAccountingArticles } from "@/lib/accounting/actions";

interface AccountingArticleMappingProps {
  venueId: number;
  itemId: number;
  itemType: "add-on" | "show" | "shop-item";
  itemName: string;
  currentArticleCode: string | null;
  accountingId?: number | null;
  userRoleId?: number; // 1 = super_admin, 2 = booking_staff
  onUpdate?: (newArticleCode: string | null) => void;
  // Additional data to preserve when saving (for shows)
  preserveData?: {
    studioAssignments?: Array<{ studio_id: number }>;
  };
}

export function AccountingArticleMapping({
  venueId,
  itemId,
  itemType,
  itemName,
  currentArticleCode,
  accountingId,
  userRoleId,
  onUpdate,
  preserveData,
}: AccountingArticleMappingProps) {
  const router = useRouter();
  const [articleCode, setArticleCode] = useState<string | null>(
    currentArticleCode,
  );
  const [articleSearchOpen, setArticleSearchOpen] = useState(false);
  const [articleSearchQuery, setArticleSearchQuery] = useState("");
  const [articleSearchResults, setArticleSearchResults] = useState<any[]>([]);
  const [articleSearchLoading, setArticleSearchLoading] = useState(false);
  const [savingArticleNumber, setSavingArticleNumber] = useState<string | null>(
    null,
  );
  const [hasAccessToken, setHasAccessToken] = useState<boolean>(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState<boolean>(true);
  const [accountingSystemName, setAccountingSystemName] = useState<string | null>(null);

  // OAuth error handler (only for systems that use OAuth)
  const {
    checkAndHandleOAuthError,
    showReauthDialog,
    setShowReauthDialog,
    accountingSystem,
  } = useOAuthErrorHandler({
    venueId,
    defaultAccountingSystem: accountingSystemName || "fortnox",
  });

  // Check if venue has valid accounting credentials
  useEffect(() => {
    const checkAccountingAuth = async () => {
      if (!accountingId) {
        setHasAccessToken(false);
        setIsCheckingAuth(false);
        return;
      }

      try {
        const response = await fetch(
          `/api/superadmin/venue/${venueId}/accounting-settings`,
        );
        if (response.ok) {
          const data = await response.json();
          setAccountingSystemName(data.settings?.accounting_system || null);

          // PowerOffice uses environment variables, not OAuth tokens
          if (data.settings?.accounting_system === 'poweroffice') {
            setHasAccessToken(true); // Always consider PowerOffice as authorized
          } else {
            setHasAccessToken(!!data.settings?.access_token);
          }
        } else {
          setHasAccessToken(false);
        }
      } catch (error) {
        console.error("Error checking accounting authorization:", error);
        setHasAccessToken(false);
      } finally {
        setIsCheckingAuth(false);
      }
    };

    checkAccountingAuth();
  }, [accountingId, venueId]);

  const handleSearchArticles = async (query: string) => {
    if (!query || query.trim().length < 2) {
      setArticleSearchResults([]);
      return;
    }

    setArticleSearchLoading(true);
    try {
      // Use server action instead of API route
      const result = await searchAccountingArticles(venueId, query);

      // Check for OAuth errors first (but not for PowerOffice)
      if (!result.success && accountingSystemName !== 'poweroffice' && checkAndHandleOAuthError(result.error || "")) {
        setArticleSearchResults([]);
        return;
      }

      if (!result.success) {
        throw new Error(result.error || "Failed to search articles");
      }

      setArticleSearchResults(result.articles || []);
    } catch (error: any) {
      // Check if it's an OAuth error (but not for PowerOffice)
      if (accountingSystemName !== 'poweroffice' && checkAndHandleOAuthError(error)) {
        // OAuth error was handled
      } else {
        toast.error(error.message || "Failed to search accounting articles");
      }
      setArticleSearchResults([]);
    } finally {
      setArticleSearchLoading(false);
    }
  };

  const saveArticleCode = async (code: string | null) => {
    setSavingArticleNumber(code || "REMOVING");
    try {
      let endpoint: string;
      let body: any;

      if (itemType === "add-on") {
        endpoint = `/api/venues/${venueId}/add-ons/${itemId}`;
        body = {
          name: itemName,
          add_on_type: "other", // Required by API
          article_code: code,
        };
      } else if (itemType === "show") {
        endpoint = `/api/venues/${venueId}/shows/${itemId}`;
        body = {
          article_code: code,
          // Include studio assignments if they exist to preserve them
          ...(preserveData?.studioAssignments && {
            studioAssignments: preserveData.studioAssignments,
          }),
        };
      } else {
        // shop-item
        endpoint = `/api/venues/${venueId}/shop-items/${itemId}`;
        body = {
          article_code: code,
        };
      }

      const response = await fetch(endpoint, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to update article mapping");
      }

      setArticleCode(code);
      toast.success(
        code ? "Article mapped successfully" : "Article mapping removed",
      );
      setArticleSearchOpen(false);
      setArticleSearchQuery("");
      setArticleSearchResults([]);

      // Call the onUpdate callback if provided
      if (onUpdate) {
        onUpdate(code);
      }

      router.refresh();
    } catch (error: any) {
      toast.error(error.message || "Failed to update article mapping");
    } finally {
      setSavingArticleNumber(null);
    }
  };

  return (
    <>
      <div className="border rounded-lg p-6 space-y-4">
        <h3 className="text-lg font-semibold">Accounting article mapping</h3>
        <p className="text-sm text-gray-600">
          Map this{" "}
          {itemType === "add-on"
            ? "add-on"
            : itemType === "show"
              ? "show"
              : "shop item"}{" "}
          to an accounting system article for integration.
        </p>

        <div className="flex items-center gap-4">
          <div className="flex-1">
            {articleCode ? (
              <div className="flex items-center gap-2">
                <Badge className="bg-blue-100 text-blue-800 border-blue-200 text-sm">
                  {articleCode}
                </Badge>
                <span className="text-sm text-gray-600">Mapped</span>
              </div>
            ) : (
              <Badge className="bg-amber-100 text-amber-800 border-amber-200 text-sm">
                Not mapped
              </Badge>
            )}
          </div>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setArticleSearchOpen(true)}
              disabled={isCheckingAuth || !accountingId || !hasAccessToken}
              title={
                isCheckingAuth
                  ? "Checking authorization..."
                  : !accountingId
                    ? "Please configure accounting system in venue settings first"
                    : !hasAccessToken
                      ? "Please authorize the accounting system in venue settings first"
                      : ""
              }
            >
              {isCheckingAuth ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Checking...
                </>
              ) : articleCode ? (
                "Change article"
              ) : (
                "Map article"
              )}
            </Button>
            {articleCode && (
              <Button
                type="button"
                variant="outline"
                onClick={() => saveArticleCode(null)}
                disabled={
                  savingArticleNumber !== null ||
                  isCheckingAuth ||
                  !accountingId ||
                  !hasAccessToken
                }
              >
                {savingArticleNumber === "REMOVING" && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                Remove mapping
              </Button>
            )}
          </div>
        </div>
        {!isCheckingAuth && !accountingId && (
          <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded-md">
            <p className="text-sm text-amber-800">
              <strong>Note:</strong> Please configure the accounting system in{" "}
              <a
                href={`/superadmin/venues/${venueId}`}
                className="underline hover:text-amber-900"
                target="_blank"
                rel="noopener noreferrer"
              >
                venue settings
              </a>{" "}
              before mapping articles.
            </p>
          </div>
        )}
        {!isCheckingAuth && accountingId && !hasAccessToken && accountingSystemName !== 'poweroffice' && (
          <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded-md">
            <p className="text-sm text-amber-800">
              <strong>Note:</strong> Please configure the accounting system in{" "}
              <a
                href={`/superadmin/venues/${venueId}`}
                className="underline hover:text-amber-900"
                target="_blank"
                rel="noopener noreferrer"
              >
                venue settings
              </a>{" "}
              before mapping articles. Ensure the accounting system is properly authorized and connected.
            </p>
          </div>
        )}
      </div>

      {/* Article Search Dialog */}
      <Dialog open={articleSearchOpen} onOpenChange={setArticleSearchOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[700px]">
          <DialogHeader>
            <DialogTitle>Search accounting articles</DialogTitle>
            <DialogDescription>
              Search for an accounting article to map to this{" "}
              {itemType === "add-on"
                ? "add-on"
                : itemType === "show"
                  ? "show"
                  : "shop item"}
              . Type at least 2 characters to search.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label htmlFor="articleSearch">Search articles</Label>
              <div className="relative mt-2">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  id="articleSearch"
                  type="text"
                  placeholder="Search by article number or description..."
                  value={articleSearchQuery}
                  onChange={(e) => {
                    setArticleSearchQuery(e.target.value);
                    handleSearchArticles(e.target.value);
                  }}
                  className="pl-10"
                  autoFocus
                />
              </div>
            </div>

            {/* Search Results */}
            <div className="border rounded-lg max-h-[400px] overflow-y-auto">
                {articleSearchLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
                  </div>
                ) : articleSearchQuery.length < 2 ? (
                  <div className="text-center py-12 text-gray-500">
                    <p className="text-sm">
                      Enter at least 2 characters to search
                    </p>
                  </div>
                ) : articleSearchResults.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <p className="text-sm">No articles found</p>
                    <p className="text-xs mt-1">
                      Try a different search term or article number
                    </p>
                  </div>
              ) : (
                <div className="divide-y">
                  {articleSearchResults.map((article: any, index: number) => (
                    <button
                      key={index}
                      type="button"
                      onClick={() => saveArticleCode(article.articleNumber)}
                      disabled={savingArticleNumber !== null}
                      className="w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <Badge
                              variant="secondary"
                              className="bg-blue-100 text-blue-800 border-blue-200"
                            >
                              {article.articleNumber}
                            </Badge>
                            {savingArticleNumber === article.articleNumber && (
                              <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
                            )}
                          </div>
                          <p className="text-sm font-medium text-gray-900 mt-1">
                            {article.description}
                          </p>
                          {article.unit && (
                            <p className="text-xs text-gray-500 mt-1">
                              Unit: {article.unit}
                            </p>
                          )}
                        </div>
                        {article.price && (
                          <div className="text-right">
                            <p className="text-sm font-medium text-gray-900">
                              {article.price}
                            </p>
                            <p className="text-xs text-gray-500">Price</p>
                          </div>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setArticleSearchOpen(false);
                setArticleSearchQuery("");
                setArticleSearchResults([]);
              }}
            >
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* OAuth Re-authorization Dialog */}
      <OAuthReauthDialog
        open={showReauthDialog}
        onOpenChange={setShowReauthDialog}
        accountingSystem={accountingSystem}
        venueId={venueId}
        userRoleId={userRoleId}
      />
    </>
  );
}
